import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

interface Tag {
  id: string;
  nome: string;
  cor: string;
  descricao: string;
}

const Etiquetas: React.FC = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [filteredTags, setFilteredTags] = useState<Tag[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [novaTag, setNovaTag] = useState({
    nome: '',
    cor: '#25D366',
    descricao: ''
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Cores disponíveis para etiquetas
  const cores = [
    { valor: '#25D366', nome: 'Verde Sinticket' },
    { valor: '#128C7E', nome: 'Verde Escuro' },
    { valor: '#34B7F1', nome: 'Azul' },
    { valor: '#F44336', nome: 'Vermelho' },
    { valor: '#FF9800', nome: 'Laranja' },
    { valor: '#FFEB3B', nome: 'Amarelo' },
    { valor: '#9C27B0', nome: 'Roxo' },
    { valor: '#E91E63', nome: 'Rosa' },
    { valor: '#795548', nome: 'Marrom' },
    { valor: '#607D8B', nome: 'Cinza Azulado' }
  ];

  // Dados de exemplo
  useEffect(() => {
    const exemploTags: Tag[] = [
      {
        id: '1',
        nome: 'Suporte',
        cor: '#25D366',
        descricao: 'Atendimentos relacionados a suporte técnico'
      },
      {
        id: '2',
        nome: 'Vendas',
        cor: '#34B7F1',
        descricao: 'Atendimentos relacionados a vendas e orçamentos'
      },
      {
        id: '3',
        nome: 'Reclamação',
        cor: '#F44336',
        descricao: 'Atendimentos com reclamações de clientes'
      },
      {
        id: '4',
        nome: 'Dúvida',
        cor: '#FF9800',
        descricao: 'Atendimentos com dúvidas gerais'
      },
      {
        id: '5',
        nome: 'Urgente',
        cor: '#E91E63',
        descricao: 'Atendimentos que precisam de atenção imediata'
      }
    ];

    setTags(exemploTags);
    setFilteredTags(exemploTags);
  }, []);

  // Filtrar tags com base na pesquisa
  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const filtered = tags.filter(
        (tag) =>
          tag.nome.toLowerCase().includes(term) ||
          tag.descricao.toLowerCase().includes(term)
      );
      setFilteredTags(filtered);
    } else {
      setFilteredTags(tags);
    }
  }, [tags, searchTerm]);

  // Adicionar nova tag
  const handleAddTag = () => {
    const newId = (tags.length + 1).toString();
    
    const novaTagObj: Tag = {
      id: newId,
      nome: novaTag.nome,
      cor: novaTag.cor,
      descricao: novaTag.descricao
    };
    
    setTags([...tags, novaTagObj]);
    setNovaTag({
      nome: '',
      cor: '#25D366',
      descricao: ''
    });
    setIsDialogOpen(false);
  };

  // Excluir tag
  const handleDeleteTag = (id: string) => {
    setTags(tags.filter(tag => tag.id !== id));
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Etiquetas</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Nova Etiqueta
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Adicionar Etiqueta</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome</Label>
                <Input
                  id="nome"
                  value={novaTag.nome}
                  onChange={(e) => setNovaTag({...novaTag, nome: e.target.value})}
                  placeholder="Ex: Suporte"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cor">Cor</Label>
                <Select
                  value={novaTag.cor}
                  onValueChange={(value) => setNovaTag({...novaTag, cor: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma cor" />
                  </SelectTrigger>
                  <SelectContent>
                    {cores.map((cor) => (
                      <SelectItem key={cor.valor} value={cor.valor}>
                        <div className="flex items-center">
                          <div 
                            className="w-4 h-4 rounded-full mr-2" 
                            style={{ backgroundColor: cor.valor }}
                          ></div>
                          {cor.nome}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição</Label>
                <Input
                  id="descricao"
                  value={novaTag.descricao}
                  onChange={(e) => setNovaTag({...novaTag, descricao: e.target.value})}
                  placeholder="Ex: Atendimentos relacionados a suporte técnico"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={handleAddTag}>Adicionar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6">
        <Input
          type="text"
          placeholder="Pesquisar etiquetas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>

      {filteredTags.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">Nenhuma etiqueta encontrada.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTags.map((tag) => (
            <Card key={tag.id} className="overflow-hidden">
              <CardHeader className="pb-2 flex justify-between items-start">
                <div className="flex items-center">
                  <div 
                    className="w-4 h-4 rounded-full mr-2" 
                    style={{ backgroundColor: tag.cor }}
                  ></div>
                  <CardTitle className="text-lg">{tag.nome}</CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-gray-500 hover:text-red-500"
                  onClick={() => handleDeleteTag(tag.id)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18" />
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                  </svg>
                </Button>
              </CardHeader>
              <CardContent className="pt-2">
                <p className="text-gray-700">{tag.descricao}</p>
                <div className="flex justify-between items-center mt-4">
                  <span className="text-xs text-gray-500">ID: {tag.id}</span>
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-medium" 
                    style={{ 
                      backgroundColor: tag.cor,
                      color: isLightColor(tag.cor) ? '#000' : '#fff'
                    }}
                  >
                    {tag.nome}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Função para determinar se uma cor é clara (para definir a cor do texto)
function isLightColor(color: string): boolean {
  // Converter hex para RGB
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calcular luminosidade
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5;
}

export default Etiquetas;
